import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/hooks/use-app-context";
import { useLanguages } from "@/hooks/use-languages";
import { Icon } from "@/lib/icons";
import type { LanguageProfileField } from "@/lib/language-profile";
import { cn } from "@/lib/utils";
import type { AuthUser } from "@/types/auth.types";
import type { SettingsCopy } from "./settings-copy";
import { SettingsCard, SettingsRow, SettingsRows } from "./settings-ui";

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-border/60 bg-surface-2 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => value !== option.value && onChange(option.value)}
          className={cn(
            "rounded px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === option.value ? "bg-surface text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Interface language and theme: the rows every profile has. */
export function InterfacePreferenceRows({ copy }: { copy: SettingsCopy }) {
  const { language, setLanguage, theme, toggleTheme } = useApp();
  return (
    <SettingsRows>
      <SettingsRow icon="globe" label={copy.interfaceLanguage} hint={copy.interfaceLanguageHint}>
        <Segmented
          label={copy.interfaceLanguage}
          value={language}
          onChange={setLanguage}
          options={[
            { value: "fr", label: "Français" },
            { value: "en", label: "English" },
          ]}
        />
      </SettingsRow>
      <SettingsRow icon={theme === "dark" ? "moon" : "sun"} label={copy.theme}>
        <Segmented
          label={copy.theme}
          value={theme}
          onChange={toggleTheme}
          options={[
            { value: "light", label: copy.themeLight },
            { value: "dark", label: copy.themeDark },
          ]}
        />
      </SettingsRow>
    </SettingsRows>
  );
}

/** Interface language, theme, and the languages the contributor works in. */
export function PreferencesCard({ user, copy, onEditLanguages }: { user: AuthUser; copy: SettingsCopy; onEditLanguages: () => void }) {
  const { t } = useApp();
  const { activeLanguages, isLoading } = useLanguages();
  const nameOf = (code: string) => {
    const match = activeLanguages.find((item) => item.code === code);
    return match ? match.displayName || match.name || code : code.toUpperCase();
  };

  // Same fields as the edit form: moderation languages only for moderators.
  const fields: LanguageProfileField[] = ["spokenLanguages", "contributionLanguages"];
  if (user.role === "moderator") fields.push("moderationLanguages");
  const groups = fields.map((field) => ({ field, label: copy.languageFields[field], codes: user[field] ?? [] }));

  return (
    <SettingsCard id="settings-preferences" title={copy.preferencesTitle}>
      <InterfacePreferenceRows copy={copy} />

      <div className="border-t border-border/60 px-4 py-4 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-surface-2 text-muted-foreground">
              <Icon name="languages" className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{copy.languagesTitle}</p>
              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{copy.languagesHint}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onEditLanguages} className="shrink-0">
            {copy.edit}
          </Button>
        </div>

        <dl className="mt-4 space-y-3 sm:pl-12">
          {groups.map((group) => (
            <div key={group.field}>
              <dt className="text-xs text-muted-foreground">{group.label}</dt>
              <dd className="mt-1.5 flex flex-wrap gap-1.5">
                {isLoading ? (
                  <Skeleton className="h-6 w-28 bg-muted/40" />
                ) : group.codes.length === 0 ? (
                  <span className="text-sm text-faint-foreground">{copy.noLanguage}</span>
                ) : (
                  group.codes.map((code) => (
                    <span key={code} className="rounded-full border border-border/60 bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-foreground">
                      {nameOf(code)}
                    </span>
                  ))
                )}
              </dd>
            </div>
          ))}
          <div>
            <dt className="text-xs text-muted-foreground">{t.preferredCommunicationLanguage}</dt>
            <dd className="mt-1 text-sm font-medium text-foreground">{(user.preferredLocale ?? "fr") === "en" ? t.english : t.french}</dd>
          </div>
        </dl>
      </div>
    </SettingsCard>
  );
}
